'use client';

import React, { useState } from 'react';
import { X, Mail, User, Phone, Send } from 'lucide-react';
import { trpc } from '@/lib/trpc/client';
import { useTranslation } from 'react-i18next';
import { Title, Text } from '@/components/ui/Headings';
import { useParams } from 'next/navigation';

interface JourneyContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  journeyTitle: string;
  journeyId: number;
  departureDate?: string; // Optional specific departure date the client is interested in
}

type TravelType = 'group' | 'private';
type PartyType = 'solo' | 'companions';

const JourneyContactModal: React.FC<JourneyContactModalProps> = ({
  isOpen,
  onClose,
  journeyTitle,
  journeyId,
  departureDate
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const locale = (params?.locale as string) || 'en';

  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    whatsapp: string;
    message: string;
    travelType: TravelType;
    partyType: PartyType;
    companionsCount: string; // keep as string for input; we will format in message
    preferredTravelDate: string;
  }>({
    name: '',
    email: '',
    whatsapp: '',
    message: '',
    travelType: 'group',
    partyType: 'solo',
    companionsCount: '',
    preferredTravelDate: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string; whatsapp?: string }>({});

  const submitMutation = trpc.travelLeads.createFromSignatureJourney.useMutation({
    onSuccess: () => {
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        whatsapp: '',
        message: '',
        travelType: 'group',
        partyType: 'solo',
        companionsCount: '',
        preferredTravelDate: ''
      });
    },
    onError: (error) => {
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to send request');
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear field-level error on change
    if (fieldErrors[e.target.name as 'name' | 'email' | 'whatsapp']) {
      setFieldErrors({ ...fieldErrors, [e.target.name]: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('idle');
    setErrorMessage('');
    setFieldErrors({});

    // Basic client-side validation
    const nextFieldErrors: { name?: string; email?: string; whatsapp?: string } = {};
    const isValidEmail = (value: string) => /.+@.+\..+/.test(value);
    const isValidPhone = (value: string) => /^\+?[0-9\s\-()]{7,}$/.test(value) || value.trim() === '';

    if (!formData.name.trim()) {
      nextFieldErrors.name = t('journeyContactModal.nameError');
    }
    if (!formData.email.trim() || !isValidEmail(formData.email)) {
      nextFieldErrors.email = t('journeyContactModal.emailError');
    }
    if (formData.whatsapp && formData.whatsapp.trim() && !isValidPhone(formData.whatsapp.trim())) {
      nextFieldErrors.whatsapp = t('journeyContactModal.whatsappError');
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setSubmitStatus('error');
      setErrorMessage(t('journeyContactModal.fixFields'));
      return;
    }

    // Determine travel type - if departure date is selected, it's always a group inquiry
    const travelType = departureDate ? 'group' : formData.travelType;
    
    const composedMessage = [
      departureDate ? `Specific departure interest: ${departureDate}` : `Booking intent: ${travelType === 'group' ? 'Join a group' : 'Private journey'}`,
      `Party: ${formData.partyType}${formData.partyType === 'companions' && formData.companionsCount ? `, ${formData.companionsCount} companions` : ''}`,
      travelType === 'private' && formData.preferredTravelDate ? `Preferred travel time: ${formData.preferredTravelDate}` : undefined,
      formData.message ? `Questions/requests: ${formData.message}` : undefined
    ].filter(Boolean).join('\n');

    submitMutation.mutate({
      name: formData.name,
      email: formData.email,
      whatsapp: formData.whatsapp || undefined,
      message: composedMessage || undefined,
      signatureJourneyId: journeyId,
      departureDate: departureDate || undefined,
      preferredLanguage: locale as 'en' | 'de' | 'uk'
    });
  };

  const handleClose = () => {
    onClose();
    setSubmitStatus('idle');
    setErrorMessage('');
    setFormData({
      name: '',
      email: '',
      whatsapp: '',
      message: '',
      travelType: 'group',
      partyType: 'solo',
      companionsCount: '',
      preferredTravelDate: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      {/* Light yellow gradient background overlay matching Our Approach */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-neutral-cream opacity-95"></div>
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] shadow-2xl my-6 flex flex-col border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-none">
          <div>
            <Title size="sm">{t('journeyContactModal.title')}</Title>
            <Text variant="caption" className="text-gray-600 mt-1">
              {t('journeyContactModal.requestInfo')} <strong>{journeyTitle}</strong>
            </Text>
            {departureDate && (
              <div className="mt-2 inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('journeyContactModal.interestedIn')} {departureDate}
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-auto">
          {/* Travel Type - Only show if no specific departure date is selected */}
          {!departureDate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('journeyContactModal.travelTypeQuestion')}</label>
              <div className="flex items-center gap-6 flex-wrap">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="travelType"
                    value="group"
                    checked={formData.travelType === 'group'}
                    onChange={handleChange}
                  />
                  {t('journeyContactModal.joinGroup')}
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="radio"
                    name="travelType"
                    value="private"
                    checked={formData.travelType === 'private'}
                    onChange={handleChange}
                  />
                  {t('journeyContactModal.privateJourney')}
                </label>
              </div>
            </div>
          )}

          {/* Party Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('journeyContactModal.partyQuestion')}</label>
            <div className="flex items-center gap-6 flex-wrap">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="partyType"
                  value="solo"
                  checked={formData.partyType === 'solo'}
                  onChange={handleChange}
                />
                {t('journeyContactModal.solo')}
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="partyType"
                  value="companions"
                  checked={formData.partyType === 'companions'}
                  onChange={handleChange}
                />
                {t('journeyContactModal.withCompanions')}
              </label>
              {formData.partyType === 'companions' && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{t('journeyContactModal.companions')}</span>
                  <input
                    type="number"
                    min={1}
                    name="companionsCount"
                    value={formData.companionsCount}
                    onChange={handleChange}
                    placeholder={t('journeyContactModal.companionsPlaceholder')}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preferred Travel Date - Only show for private journeys and when no specific departure date is selected */}
          {!departureDate && formData.travelType === 'private' && (
            <div>
              <label htmlFor="preferredTravelDate" className="block text-sm font-medium text-gray-700 mb-1">
                {t('journeyContactModal.preferredTravelTime')}
              </label>
              <input
                type="text"
                id="preferredTravelDate"
                name="preferredTravelDate"
                value={formData.preferredTravelDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder={t('journeyContactModal.preferredTravelTimePlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('journeyContactModal.preferredTravelTimeHint')}
              </p>
            </div>
          )}

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              <User className="h-4 w-4 inline mr-2" />
              {t('journeyContactModal.fullName')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder={t('journeyContactModal.fullNamePlaceholder')}
            />
            {fieldErrors.name && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              <Mail className="h-4 w-4 inline mr-2" />
              {t('journeyContactModal.emailAddress')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder={t('journeyContactModal.emailAddressPlaceholder')}
            />
            {fieldErrors.email && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
            )}
          </div>

          {/* WhatsApp Field */}
          <div>
            <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">
              <Phone className="h-4 w-4 inline mr-2" />
              {t('journeyContactModal.whatsappNumber')}
            </label>
            <input
              type="tel"
              id="whatsapp"
              name="whatsapp"
              value={formData.whatsapp}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder={t('journeyContactModal.whatsappPlaceholder')}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('journeyContactModal.whatsappHint')}
            </p>
            {fieldErrors.whatsapp && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.whatsapp}</p>
            )}
          </div>

          {/* Combined Questions/Requests Field */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              {t('journeyContactModal.additionalQuestions')}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder={t('journeyContactModal.additionalQuestionsPlaceholder')}
            />
          </div>

          {/* Submit Button */}
          <div className="sticky bottom-0 bg-white pt-2">
            <button
              type="submit"
              disabled={submitMutation.isPending}
              className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-550 transition-colors flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {submitMutation.isPending ? t('journeyContactModal.sendingRequest') : t('journeyContactModal.getDetails')}
              <Send className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-center">
                {t('journeyContactModal.successMessage')}
              </p>
            </div>
          )}

          {submitStatus === 'error' && errorMessage && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-center">
                {errorMessage || t('journeyContactModal.errorMessage')}
              </p>
            </div>
          )}
        </form>

      </div>
    </div>
  );
};

export default JourneyContactModal;
